// layoutFieldDisplay.js

// Utilities
import { formatPrice, resolveFieldCurrencyCode } from 'utilities/Currency';
import { normalizeSchemaFieldType } from 'utilities/FieldTypes';
import { formatLocationDisplayValue } from 'utilities/Location';
import { formatMeasurementValue } from 'utilities/Measurement';
import { getNodeSubtitle, isSubtitleFieldKey } from 'utilities/NodeSubtitle';
import { formatTemporalFieldValue, isTemporalFieldType } from 'views/utils/temporalFieldFormat';
import { filterSecuredSchemaFields } from 'views/utils/displaySchemaFields';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';
import { getProfilePeople, getProfilePersonName } from 'profile/ProfileFieldValueUtils';

const STANDARD_FIELD_DEFINITIONS = [
	{ key: 'title', name: 'title', label: 'Title', synthetic: true, type: 'string' },
	{ key: 'autonumber', name: 'autonumber', label: 'ID', synthetic: true, type: 'number' },
	{ key: 'autokey', name: 'autokey', label: 'Key', synthetic: true, type: 'string' },
	{ key: 'subtitle', name: 'subtitle', label: 'Subtitle', synthetic: true, type: 'subtitle' },
	{ key: 'description', name: 'description', label: 'Subtitle', synthetic: true, type: 'subtitle' },
	{ key: 'tags', name: 'tags', label: 'Tags', synthetic: true, type: 'tags' },
	{ key: 'dateCreated', name: 'dateCreated', label: 'Date Created', synthetic: true, type: 'datetime' },
	{ key: 'dateModified', name: 'dateModified', label: 'Date Modified', synthetic: true, type: 'datetime' },
	{ key: 'cover', name: 'cover', label: 'Cover', synthetic: true, type: 'cover' },
	{ key: 'coverUrl', name: 'coverUrl', label: 'Cover URL', synthetic: true, type: 'url' },
];

function normalizeString(value) {
	return typeof value === 'string' ? value.trim() : '';
}

function getFieldKey(fieldDef) {
	return normalizeString(fieldDef?.name || fieldDef?.key);
}

function getFieldLabel(fieldDef) {
	return normalizeString(fieldDef?.label || fieldDef?.title || fieldDef?.name || fieldDef?.key);
}

function getSchemaFields(typeDef) {
	return filterSecuredSchemaFields(
		Array.isArray(typeDef?.schema?.fields)
			? typeDef.schema.fields.filter(Boolean)
			: [],
	);
}

function buildFieldDefinitionMap(typeDef) {
	const map = new Map();

	STANDARD_FIELD_DEFINITIONS.forEach((fieldDef) => {
		const key = getFieldKey(fieldDef);
		if (!key || map.has(key)) return;
		map.set(key, fieldDef);
	});

	const schemaFields = getSchemaFields(typeDef);

	schemaFields.forEach((fieldDef) => {
		const key = getFieldKey(fieldDef);
		if (!key) return;
		map.set(key, fieldDef);
	});

	return map;
}

function resolveFieldBlockValue({
	dataSection,
	fieldDef,
	item,
	typeKey,
}) {
	if (!item || !fieldDef) return undefined;

	const fieldKey = getFieldKey(fieldDef);
	const fieldType = normalizeSchemaFieldType(fieldDef?.type);

	if (fieldKey === 'title') return item?.title;
	if (fieldKey === 'autonumber') return item?.autonumber;
	if (fieldKey === 'autokey') return item?.autokey;
	if (fieldKey === 'dateCreated') return item?.dateCreated;
	if (fieldKey === 'dateModified') return item?.dateModified;
	if (fieldKey === 'tags' || fieldType === 'tags') return item?.tags;
	if (isSubtitleFieldKey(fieldKey, fieldType)) return getNodeSubtitle(item);
	if (fieldKey === 'coverUrl') return item?.coverUrl || item?.coverStoragePath || '';
	if (fieldKey === 'cover' || fieldType === 'cover') return item?.coverUrl || item?.coverStoragePath || '';

	return resolveSchemaFieldValue({
		dataSection,
		fieldDef,
		item,
		typeKey,
	});
}

function normalizeScalarDisplayValue(value) {
	if (value == null) return '';
	if (typeof value === 'string') return value.trim();
	if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
	if (typeof value === 'boolean') return value ? 'Yes' : 'No';
	if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toLocaleString();

	if (Array.isArray(value)) {
		return value
			.map((entry) => normalizeScalarDisplayValue(entry))
			.filter(Boolean)
			.join(', ');
	}

	if (typeof value === 'object') {
		const candidateValue = value?.label
			?? value?.title
			?? value?.name
			?? value?.text
			?? value?.address
			?? value?.fullAddress
			?? value?.displayName
			?? value?.email
			?? value?.phone
			?? value?.value;

		return normalizeScalarDisplayValue(candidateValue);
	}

	return '';
}

function formatPercentageValue(value) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) return '';

	const clampedValue = Math.max(0, Math.min(100, Math.round(numericValue)));
	return `${clampedValue}%`;
}

function formatNodelistValue(value, nodesByKey) {
	const values = Array.isArray(value) ? value : [value];

	return values
		.map((entry) => {
			if (entry == null) return '';

			if (typeof entry === 'string') {
				const nodeTitle = normalizeString(nodesByKey?.[entry]?.title);
				return nodeTitle;
			}

			if (typeof entry === 'object') {
				const nodeKey = normalizeString(entry?.key || entry?.nodeKey || entry?.id);
				const referencedNodeTitle = nodeKey
					? normalizeString(nodesByKey?.[nodeKey]?.title)
					: '';
				const directTitle = normalizeScalarDisplayValue(entry);

				return referencedNodeTitle || directTitle;
			}

			return '';
		})
		.filter(Boolean)
		.join(', ');
}

function formatPeopleValue(value) {
	return getProfilePeople(value)
		.map((person) => getProfilePersonName(person))
		.filter(Boolean)
		.join(', ');
}

function formatFieldValue(value, fieldDef, { nodesByKey } = {}) {
	const fieldType = normalizeSchemaFieldType(fieldDef?.type);

	if (fieldType === 'cover') {
		return normalizeString(value) ? 'Image attached' : '';
	}

	if (fieldType === 'boolean') return value ? 'Yes' : 'No';

	if (fieldType === 'money') {
		if (value == null || value === '') return '';
		return formatPrice(value, resolveFieldCurrencyCode(fieldDef));
	}

	if (fieldType === 'currency') {
		return String(value || '').trim().toUpperCase() || '';
	}

	if (fieldType === 'measurement') {
		return formatMeasurementValue(value);
	}

	if (fieldType === 'relation') {
		return formatNodelistValue(value, nodesByKey);
	}

	if (fieldType === 'people') {
		return formatPeopleValue(value);
	}

	if (fieldType === 'checklist') {
		const totalItems = Number(value?.totalItems) || 0;
		const completedItems = Number(value?.completedItems) || 0;
		if (totalItems > 0) {
			return `${completedItems}/${totalItems}`;
		}

		if (Array.isArray(value)) {
			if (!value.length) return '';
			const doneCount = value.filter((item) => item?.completed || item?.checked || item?.status === 'done').length;
			return `${doneCount}/${value.length}`;
		}

		return '';
	}

	if (fieldType === 'location') {
		return formatLocationDisplayValue(value);
	}

	if (fieldType === 'percentage') {
		return formatPercentageValue(value);
	}

	if ((fieldType === 'annualdate' || isTemporalFieldType(fieldType)) && value) {
		const formattedTemporalValue = formatTemporalFieldValue({
			fieldType,
			value,
		});

		if (formattedTemporalValue) {
			return formattedTemporalValue;
		}
	}

	return normalizeScalarDisplayValue(value);
}

export function normalizeFieldBlockFieldKeys(fieldKeys) {
	return Array.from(new Set(
		(Array.isArray(fieldKeys) ? fieldKeys : [])
			.map((fieldKey) => normalizeString(fieldKey))
			.filter(Boolean)
	));
}

export function getFieldBlockOptions(typeDef) {
	return Array.from(buildFieldDefinitionMap(typeDef).values())
		.filter((fieldDef) => {
			const key = getFieldKey(fieldDef);
			const isSynthetic = fieldDef?.synthetic === true;
			const fieldType = normalizeSchemaFieldType(fieldDef?.type);

			if (
				key === 'comments'
				|| fieldType === 'comments'
				|| key === 'activity'
				|| fieldType === 'activity'
			) {
				return false;
			}

			if ((key === 'subtitle' || key === 'description') && isSynthetic) {
				return false;
			}

			if (!isSynthetic) {
				return true;
			}

			return key === 'title';
		})
		.map((fieldDef) => {
			const key = getFieldKey(fieldDef);
			if (!key) return null;

			const label = getFieldLabel(fieldDef) || key;
			const typeLabel = normalizeString(fieldDef?.type || 'field');

			return {
				fieldDef,
				key,
				label,
				typeLabel,
			};
		})
		.filter(Boolean);
}

export function buildFieldBlockRows({
	fieldKeys,
	item,
	nodesByKey = null,
	typeDef,
}) {
	if (!item || !typeDef) return [];

	const typeKey = normalizeString(typeDef?.key || typeDef?.type);
	const dataSection = normalizeString(typeDef?.dataSection);
	const fieldByKey = buildFieldDefinitionMap(typeDef);
	const normalizedFieldKeys = normalizeFieldBlockFieldKeys(fieldKeys);
	const selectedFieldKeySet = new Set(normalizedFieldKeys);
	const orderedFieldKeys = getFieldBlockOptions(typeDef)
		.map((option) => option.key)
		.filter((fieldKey) => selectedFieldKeySet.has(fieldKey));

	return orderedFieldKeys
		.map((fieldKey) => {
			const fieldDef = fieldByKey.get(fieldKey);
			const label = fieldDef ? (getFieldLabel(fieldDef) || fieldKey) : fieldKey;
			const fieldType = normalizeSchemaFieldType(fieldDef?.type, 'field');
			const value = fieldDef
				? resolveFieldBlockValue({
					dataSection,
					fieldDef,
					item,
					typeKey,
				})
				: undefined;
			const peopleProfiles = fieldType === 'people' ? getProfilePeople(value) : [];
			const valueText = fieldDef ? formatFieldValue(value, fieldDef, { nodesByKey }) : '';

			return {
				fieldDef: fieldDef || { label, name: fieldKey, type: fieldType },
				fieldKey,
				fieldType,
				isEmpty: !valueText && !peopleProfiles.length,
				label,
				peopleProfiles,
				value,
				valueText,
			};
		});
}